import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Pencil, Check, X, Handshake } from "lucide-react";
import { BackButton } from "@/components/BackButton";

interface ConstructionSite {
  id: string;
  name: string;
  address: string | null;
}

export default function ProfileSettings() {
  const { t } = useI18n();
  const { user, isReady } = useAuth();
  const navigate = useNavigate();
  
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [sites, setSites] = useState<ConstructionSite[]>([]);
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteAddress, setNewSiteAddress] = useState("");
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSupplier, setIsSupplier] = useState(false);

  useEffect(() => {
    if (isReady && !user) navigate("/login");
  }, [isReady, user, navigate]);

  useEffect(() => {
    if (!user) return;
    loadProfile();
    loadSites();
    supabase.from("suppliers").select("id").eq("user_id", user.id).maybeSingle().then(({ data }) => {
      setIsSupplier(!!data);
    });
  }, [user]);

  const loadProfile = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("full_name, phone")
      .eq("user_id", user!.id)
      .single();
    if (data) {
      setFullName(data.full_name || "");
      setPhone(data.phone || "");
    }
  };

  const loadSites = async () => {
    const { data } = await supabase
      .from("construction_sites")
      .select("*")
      .eq("user_id", user!.id)
      .order("created_at", { ascending: true });
    if (data) setSites(data);
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName || null, phone: phone || null, updated_at: new Date().toISOString() })
      .eq("user_id", user!.id);
    setLoading(false);
    if (error) toast.error(error.message);
    else toast.success(t("profile.saved"));
  };

  const handleAddSite = async () => {
    if (!newSiteName.trim()) return;
    const { error } = await supabase
      .from("construction_sites")
      .insert({ user_id: user!.id, name: newSiteName.trim(), address: newSiteAddress.trim() || null });
    if (error) toast.error(error.message);
    else {
      toast.success(t("profile.siteAdded"));
      setNewSiteName("");
      setNewSiteAddress("");
      loadSites();
    }
  };

  const handleDeleteSite = async (id: string) => {
    await supabase.from("construction_sites").delete().eq("id", id);
    toast.success(t("profile.siteDeleted"));
    loadSites();
  };

  const handleEditSite = (site: ConstructionSite) => {
    setEditingSiteId(site.id);
    setEditName(site.name);
    setEditAddress(site.address || "");
  };

  const handleSaveEdit = async () => {
    if (!editingSiteId || !editName.trim()) return;
    await supabase.from("construction_sites").update({ name: editName.trim(), address: editAddress.trim() || null }).eq("id", editingSiteId);
    toast.success(t("profile.siteSaved"));
    setEditingSiteId(null);
    loadSites();
  };

  if (!isReady || !user) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-4 py-2 h-12 border-b border-border">
        <BackButton to="/" />
        <div className="flex items-center gap-1.5">
          {isSupplier && (
            <Link
              to="/supplier/dashboard"
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <Handshake className="w-4 h-4" />
              {t("profile.supplierPanel")}
            </Link>
          )}
          <button
            onClick={async () => { await supabase.auth.signOut(); navigate("/"); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-accent transition-colors text-muted-foreground text-sm"
            title={t("sidebar.logout")}
          >
            <LogOut className="w-4 h-4" />
            {t("sidebar.logout")}
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 py-8 space-y-8">
            <h1 className="text-2xl font-semibold text-foreground">{t("profile.title")}</h1>

            {/* Profile info */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-foreground">{t("profile.personalInfo")}</h2>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t("profile.fullName")}</label>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder={t("profile.fullNamePlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t("profile.phone")}</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder={t("profile.phonePlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <input
                  value={user.email || ""}
                  disabled
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-muted text-muted-foreground text-sm"
                />
              </div>
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? "..." : t("profile.save")}
              </button>
            </div>

            {/* Construction sites */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-foreground">{t("profile.sitesTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("profile.sitesSubtitle")}</p>

              {sites.map((site) => (
                <div key={site.id} className="flex items-center justify-between p-3 rounded-xl border border-border bg-card">
                  {editingSiteId === site.id ? (
                    <>
                      <div className="flex-1 space-y-1.5 mr-2">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                          placeholder={t("profile.siteNamePlaceholder")}
                        />
                        <input
                          value={editAddress}
                          onChange={(e) => setEditAddress(e.target.value)}
                          className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                          placeholder={t("profile.siteAddressPlaceholder")}
                        />
                      </div>
                      <div className="flex gap-1">
                        <button onClick={handleSaveEdit} className="p-2 rounded-lg hover:bg-primary/10 text-primary">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingSiteId(null)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm font-medium text-foreground">{site.name}</p>
                        {site.address && <p className="text-xs text-muted-foreground">{site.address}</p>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => handleEditSite(site)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteSite(site.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              <div className="space-y-2 p-4 rounded-xl border border-dashed border-border">
                <input
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder={t("profile.siteNamePlaceholder")}
                />
                <input
                  value={newSiteAddress}
                  onChange={(e) => setNewSiteAddress(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder={t("profile.siteAddressPlaceholder")}
                />
                <button
                  onClick={handleAddSite}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  {t("profile.addSite")}
                </button>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}
